import React, { useMemo, useState } from 'react';
import TopNavBar from '../../components/TopNavBar/TopNavBar';
import styles from './MoonCards.module.css';

import newMoon from '../../assets/moon-cards/newmoon.png';
import waxingCrescent from '../../assets/moon-cards/waxingcrescent_bright.png';
import firstQuarter from '../../assets/moon-cards/firstquarter_bright.png';
import waxingGibbous from '../../assets/moon-cards/waxinggibbous_bright.png';
import fullMoon from '../../assets/moon-cards/fullmoon.png';
import waningGibbous from '../../assets/moon-cards/waninggibbous_bright.png';
import thirdQuarter from '../../assets/moon-cards/thirdquarter_bright.png';
import waningCrescent from '../../assets/moon-cards/waningcrescent_bright.png';

type PhaseCard = {
  id: string;
  label: string;
  image: string;
};

const phases: PhaseCard[] = [
  { id: 'newmoon', label: 'New Moon', image: newMoon },
  { id: 'waxingcrescent_bright', label: 'Waxing Crescent', image: waxingCrescent },
  { id: 'firstquarter_bright', label: 'First Quarter', image: firstQuarter },
  { id: 'waxinggibbous_bright', label: 'Waxing Gibbous', image: waxingGibbous },
  { id: 'fullmoon', label: 'Full Moon', image: fullMoon },
  { id: 'waninggibbous_bright', label: 'Waning Gibbous', image: waningGibbous },
  { id: 'thirdquarter_bright', label: 'Third Quarter', image: thirdQuarter },
  { id: 'waningcrescent_bright', label: 'Waning Crescent', image: waningCrescent },
];

const createEmptyPlacements = () =>
  phases.reduce<Record<string, string | null>>((acc, phase) => {
    acc[phase.id] = null;
    return acc;
  }, {});

const shuffle = <T,>(items: T[]) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const MoonCards: React.FC = () => {
  const [placements, setPlacements] = useState<Record<string, string | null>>(() => createEmptyPlacements());
  const [deck, setDeck] = useState<PhaseCard[]>(() => shuffle(phases));
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);

  const placedIds = useMemo(() => new Set(Object.values(placements).filter(Boolean) as string[]), [placements]);
  const availableCards = useMemo(() => deck.filter((card) => !placedIds.has(card.id)), [deck, placedIds]);
  const completed = availableCards.length === 0;

  const resetGame = () => {
    setPlacements(createEmptyPlacements());
    setDeck(shuffle(phases));
    setFeedback(null);
  };

  const handleDrop = (slotId: string, cardId: string) => {
    if (placements[slotId]) return;
    if (slotId === cardId) {
      setPlacements((prev) => ({ ...prev, [slotId]: cardId }));
      setFeedback('correct');
    } else {
      setFeedback('wrong');
    }
    setTimeout(() => setFeedback(null), 1100);
  };

  const onDragStart = (event: React.DragEvent<HTMLDivElement>, cardId: string) => {
    event.dataTransfer.setData('text/plain', cardId);
  };

  const onDragOver = (event: React.DragEvent<HTMLDivElement>, slotId: string) => {
    if (!placements[slotId]) {
      event.preventDefault();
    }
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>, slotId: string) => {
    event.preventDefault();
    const cardId = event.dataTransfer.getData('text/plain');
    if (cardId) {
      handleDrop(slotId, cardId);
    }
  };

  return (
    <div className={styles.page}>
      <TopNavBar />
      <main className={styles.content}>
        <header className={styles.header}>
          <h1>Do You Know Your Moon Phases?</h1>
          <h2>
            <span>Move each moon phase to its corresponding card</span>
          </h2>
        </header>

        <div className={styles.actions}>
          <button type="button" onClick={resetGame} className={styles.shuffleButton}>
            Shuffle The Deck Again
          </button>
          {completed && <span className={styles.completeTag}>All 8 matched!</span>}
        </div>

        <section className={styles.playArea}>
          <div className={styles.cardRow}>
            {availableCards.map((card) => (
              <div
                key={card.id}
                className={styles.card}
                draggable
                onDragStart={(event) => onDragStart(event, card.id)}
              >
                <img src={card.image} alt={card.label} />
              </div>
            ))}
            {availableCards.length === 0 && <p className={styles.emptyState}>All cards placed! Shuffle to play again.</p>}
          </div>

          <div className={styles.slotRow}>
            {phases.map((phase) => {
              const placedCardId = placements[phase.id];
              const placedCard = deck.find((card) => card.id === placedCardId);
              return (
                <div
                  key={phase.id}
                  className={`${styles.slot} ${placedCard ? styles.slotFilled : ''}`}
                  onDragOver={(event) => onDragOver(event, phase.id)}
                  onDrop={(event) => onDrop(event, phase.id)}
                >
                  <div className={styles.slotLabel}>{phase.label}</div>
                  <div className={styles.slotTarget}>
                    {placedCard ? <img src={placedCard.image} alt={phase.label} /> : ''}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {feedback && (
          <div className={`${styles.feedback} ${feedback === 'correct' ? styles.feedbackSuccess : styles.feedbackError}`}>
            {feedback === 'correct' ? 'Nice match!' : 'Try that again.'}
          </div>
        )}
      </main>
    </div>
  );
};

export default MoonCards;
